import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PencilWritingIcon } from '@navikt/aksel-icons';
import { SurveyFormData } from '../SurveyFormCard';
import { SurveyFormCardUtils } from '../../../utils/SurveyFormCardUtils/SurveyFormCard.utils';
import { useGetDistributionChannelsQuery } from '../../../hooks';
import { useGetWorkEnvironmentTemplatesQuery } from '../../../hooks/queries/useGetWorkEnvironmentTemplatesQuery';
import { useGetSurveyTemplatesQuery } from '../../../hooks/queries/useGetSurveyTemplatesQuery';
import { useGetCustomSurveyTemplatesQuery } from '../../../hooks/queries/useGetCustomSurveyTemplatesQuery';
import { getChannelDisplayValue } from '../SurveyDistributionChannels/channelDisplay';
import { getScheduledDate, normalizeTime } from '../SurveyTimeline/surveyTimeline.utils';
import classes from './SurveyFormSummary.module.css';
import { templateVersionLabel } from '../SurveySetup/hmsTemplateView';

type SurveyFormSummaryProps = {
  formData: SurveyFormData;
  teamName: string;
  onEditStep: (stepIndex: number) => void;
};

type SummarySectionProps = {
  title: string;
  onEdit: () => void;
  children: ReactNode;
};

const SummarySection = ({ title, onEdit, children }: SummarySectionProps) => {
  const { t } = useTranslation();
  return (
    <section className={classes.section}>
      <div className={classes.sectionHeader}>
        <h2 className={`ds-heading ${classes.sectionHeading}`} data-size="2xs">
          {title}
        </h2>
        <button
          type="button"
          className="ds-button"
          data-variant="tertiary"
          data-size="sm"
          aria-label={t('surveyCreation.summary.editLabel', { section: title })}
          onClick={onEdit}
        >
          <PencilWritingIcon aria-hidden="true" fontSize="1.125rem" />
          {t('surveyCreation.summary.edit')}
        </button>
      </div>
      <dl className={classes.list}>{children}</dl>
    </section>
  );
};

const Row = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className={classes.row}>
    <dt className={`ds-paragraph ${classes.label}`} data-size="sm">
      {label}
    </dt>
    <dd className={`ds-label ${classes.value}`} data-size="sm">
      {value}
    </dd>
  </div>
);

export const SurveyFormSummary = ({
  formData,
  teamName,
  onEditStep,
}: SurveyFormSummaryProps) => {
  const { t, i18n } = useTranslation();
  const { data: distributionChannels } = useGetDistributionChannelsQuery(
    formData.team,
  );
  const { data: workEnvironmentTemplates } = useGetWorkEnvironmentTemplatesQuery(
    formData.surveyType === 'work-environment',
  );
  const { data: psychologicalSafetyTemplates } = useGetSurveyTemplatesQuery();
  const { data: customTemplates } = useGetCustomSurveyTemplatesQuery({
    teamId: formData.team,
  });

  const workEnvironmentVersion = workEnvironmentTemplates?.find(
    (version) => version.id === formData.workEnvironmentTemplateId,
  );
  const templateName =
    formData.surveyType === 'psychological-safety'
      ? psychologicalSafetyTemplates?.find(
          (template) => template.id === formData.psychologicalSafetyTemplateId,
        )?.name
      : formData.surveyType === 'work-environment'
        ? workEnvironmentVersion && templateVersionLabel(workEnvironmentVersion, t)
        : customTemplates?.find(
            (template) => template.id === formData.customTemplateId,
          )?.name;

  const locale = i18n.language === 'en' ? 'en-GB' : 'nb-NO';
  const scheduledDate = formData.isPlanning
    ? getScheduledDate(formData.date, formData.time)
    : null;
  const sendText = scheduledDate
    ? t('surveyCreation.timeline.preview.at', {
        date: scheduledDate.toLocaleDateString(locale, {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        }),
        time: normalizeTime(formData.time),
      })
    : t('surveyCreation.summary.sendNow');

  const days = (count?: number) =>
    t('surveyCreation.timeline.days', { count: count ?? 0 });

  return (
    <div className={classes.container}>
      <SummarySection title={t('surveyCreation.summary.setup')} onEdit={() => onEditStep(0)}>
        <Row label={t('surveyCreation.summary.name')} value={formData.name} />
        <Row label={t('surveyCreation.summary.team')} value={teamName} />
        <Row
          label={t('surveyCreation.summary.type')}
          value={SurveyFormCardUtils.getSurveyTypeLabel(formData.surveyType)}
        />
        <Row label={t('surveyCreation.summary.template')} value={templateName ?? '–'} />
      </SummarySection>

      <SummarySection
        title={t('surveyCreation.summary.distributionChannels')}
        onEdit={() => onEditStep(1)}
      >
        {distributionChannels && distributionChannels.length > 0 ? (
          distributionChannels.map((channel) => (
            <Row
              key={channel.id}
              label={channel.type}
              value={getChannelDisplayValue(channel)}
            />
          ))
        ) : (
          <p className={`ds-paragraph ${classes.empty}`} data-size="sm">
            {t('surveyCreation.summary.noChannels')}
          </p>
        )}
      </SummarySection>

      <SummarySection title={t('surveyCreation.summary.deadlines')} onEdit={() => onEditStep(2)}>
        <Row label={t('surveyCreation.summary.sending')} value={sendText} />
        <Row
          label={t('surveyCreation.summary.deadline')}
          value={t('surveyCreation.summary.deadlineValue', {
            count: formData.deadlineInDays,
          })}
        />
        <Row
          label={t('surveyCreation.summary.reminder')}
          value={
            formData.daysBeforeDeadlineToNotify > 0
              ? t('surveyCreation.summary.reminderValue', {
                  count: formData.daysBeforeDeadlineToNotify,
                })
              : t('surveyCreation.summary.notSet')
          }
        />
        {formData.surveyType === 'work-environment' &&
          distributionChannels &&
          distributionChannels.length > 0 && (
            <>
              <Row
                label={t('surveyCreation.summary.actionProposalDeadline')}
                value={days(formData.actionProposalDeadlineInDays)}
              />
              <Row
                label={t('surveyCreation.summary.actionVotingDeadline')}
                value={days(formData.actionVotingDeadlineInDays)}
              />
            </>
          )}
        {formData.isPlanning && (
          <Row
            label={t('surveyCreation.summary.repeats')}
            value={
              formData.isRepeating && formData.frequency
                ? SurveyFormCardUtils.getFrequencyLabel(formData.frequency)
                : t('surveyCreation.summary.no')
            }
          />
        )}
      </SummarySection>
    </div>
  );
};
